import React, { useState, useEffect } from 'react';
import { X, Music, Loader2, ListMusic, ChevronLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PlaylistPickerModal = ({ 
  isOpen, 
  onClose,
  // Crate context props
  isAddingToCrate,
  targetCrateId,
  targetCrateName,
  onTrackAddedToCrate,
  onReturnToCrate
}) => {
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistTracks, setPlaylistTracks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addingTrackId, setAddingTrackId] = useState(null);

  // Fetch user's BEATSEEK playlists
  const fetchPlaylists = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/api/playlists/my-playlists`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch playlists');
      const data = await response.json();
      setPlaylists(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
      setPlaylists([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch playlist tracks
  const fetchPlaylistTracks = async (playlistId) => {
    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/api/playlists/${playlistId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch playlist');
      const data = await response.json();
      setPlaylistTracks(Array.isArray(data.tracks) ? data.tracks : []);
    } catch (err) {
      setError(err.message);
      setPlaylistTracks([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load playlists on mount
  useEffect(() => {
    if (isOpen) {
      fetchPlaylists();
      setSelectedPlaylist(null);
      setPlaylistTracks([]);
    }
  }, [isOpen]);

  // Handle selecting a playlist
  const handleSelectPlaylist = (playlist) => {
    setSelectedPlaylist(playlist);
    fetchPlaylistTracks(playlist.id);
  };

  // Handle adding track to crate
  const handleAddToCrate = async (track) => {
    if (!onTrackAddedToCrate) return;
    setAddingTrackId(track.track_id || track.id);
    try {
      await onTrackAddedToCrate(track);
    } catch (err) {
      console.error('Error adding track:', err);
    } finally {
      setAddingTrackId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="jam-modal-overlay" 
      onClick={onClose}
      style={{ zIndex: 100000 }}
    >
      {/* Gradient Border Wrapper - Cyan/Purple for BEATSEEK branding */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          padding: '3px',
          borderRadius: '24px',
          background: 'linear-gradient(90deg, #00ffff, #a855f7, #00ffff)',
          backgroundSize: '200% 100%',
          animation: 'gradientBorderPlaylist 3s ease infinite',
          maxWidth: '900px',
          width: '90%',
          maxHeight: '85vh',
          boxShadow: '0 0 30px rgba(0, 255, 255, 0.4), 0 0 60px rgba(168, 85, 247, 0.3)'
        }}
      >
        <style>{`
          @keyframes gradientBorderPlaylist {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}</style>
        
        <div style={{
          background: 'rgba(20, 20, 35, 0.98)',
          borderRadius: '21px',
          width: '100%',
          maxHeight: 'calc(85vh - 6px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            padding: '1.5rem 2rem',
            borderBottom: '1px solid rgba(0, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <ListMusic size={28} style={{ color: '#00ffff' }} />
              <h2 style={{ margin: 0, color: 'white', fontSize: '1.3rem' }}>
                {selectedPlaylist ? selectedPlaylist.name : 'My BEATSEEK Playlists'}
              </h2>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'rgba(255, 255, 255, 0.6)',
                cursor: 'pointer',
                padding: '0.5rem'
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Crate Context Banner */}
          {isAddingToCrate && targetCrateName && (
            <div style={{
              padding: '0.75rem 2rem',
              background: 'rgba(0, 255, 255, 0.05)',
              borderBottom: '1px solid rgba(0, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#00ffff',
                fontSize: '0.9rem'
              }}>
                📦 Adding to: <strong>{targetCrateName}</strong>
              </div>
              <button
                onClick={onReturnToCrate}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'transparent',
                  border: '2px solid rgba(255, 0, 255, 0.6)',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  color: '#ff00ff',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 0 15px rgba(255, 0, 255, 0.3)',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 0, 255, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 0, 255, 0.3)';
                }}
              >
                ← Return to Crate
              </button>
            </div>
          )}

          {/* Back Button when viewing playlist tracks */}
          {selectedPlaylist && (
            <div style={{ padding: '0.75rem 2rem', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
              <button
                onClick={() => {
                  setSelectedPlaylist(null);
                  setPlaylistTracks([]);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#00ffff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.9rem',
                  padding: 0
                }}
              >
                <ChevronLeft size={18} /> Back to Playlists
              </button>
            </div>
          )}

          {/* Content */}
          <div style={{
            flex: 1,
            overflow: 'auto',
            padding: '1rem 2rem'
          }}>
            {isLoading ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '3rem',
                color: 'rgba(255, 255, 255, 0.5)'
              }}>
                <Loader2 size={32} className="spinning" style={{ marginRight: '1rem' }} />
                Loading...
              </div>
            ) : error ? (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#f87171'
              }}>
                {error}
              </div>
            ) : !selectedPlaylist ? (
              // Playlists Grid
              playlists.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: 'rgba(255, 255, 255, 0.5)'
                }}>
                  <ListMusic size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
                  <p>No playlists yet</p>
                  <p style={{ fontSize: '0.85rem' }}>
                    Create playlists in BEATSEEK to see them here
                  </p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: '1rem'
                }}>
                  {playlists.map((playlist) => (
                    <div
                      key={playlist.id}
                      onClick={() => handleSelectPlaylist(playlist)}
                      style={{
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        padding: '1rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'rgba(0, 255, 255, 0.08)';
                        e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.3)';
                        e.currentTarget.style.transform = 'translateY(-4px)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <div style={{
                        width: '100%',
                        aspectRatio: '1',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        marginBottom: '0.75rem',
                        background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.1), rgba(168, 85, 247, 0.1))'
                      }}>
                        {playlist.artwork_url || playlist.cover_url ? (
                          <img 
                            src={playlist.artwork_url || playlist.cover_url} 
                            alt="" 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Music size={48} style={{ color: 'rgba(0, 255, 255, 0.3)' }} />
                          </div>
                        )}
                      </div>
                      <div style={{ color: 'white', fontWeight: 500, fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                        {playlist.name}
                      </div>
                      <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8rem' }}>
                        {playlist.tracks?.length || 0} tracks
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              // Playlist Tracks
              playlistTracks.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '2rem',
                  color: 'rgba(255, 255, 255, 0.5)'
                }}>
                  No tracks in this playlist
                </div>
              ) : (
                <div>
                  {playlistTracks.map((track) => (
                    <div
                      key={track.track_id || track.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        marginBottom: '0.5rem',
                        background: 'rgba(255, 255, 255, 0.02)',
                        transition: 'background 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                      onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)'}
                    >
                      <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: '6px',
                        overflow: 'hidden',
                        flexShrink: 0,
                        background: 'rgba(0, 255, 255, 0.1)'
                      }}>
                        {track.artwork_url || track.artwork ? (
                          <img 
                            src={track.artwork_url || track.artwork} 
                            alt="" 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Music size={20} style={{ color: 'rgba(0, 255, 255, 0.5)' }} />
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          color: 'white',
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {track.title}
                        </div>
                        <div style={{
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontSize: '0.8rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {track.artist}
                        </div>
                      </div>
                      {track.source && (
                        <div style={{
                          fontSize: '0.7rem',
                          padding: '0.2rem 0.5rem',
                          background: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '4px',
                          color: 'rgba(255, 255, 255, 0.4)'
                        }}>
                          {track.source}
                        </div>
                      )}
                      {isAddingToCrate && (
                        <button
                          onClick={() => handleAddToCrate(track)}
                          disabled={addingTrackId === (track.track_id || track.id)}
                          style={{
                            background: 'rgba(0, 255, 255, 0.1)',
                            border: '1px solid rgba(0, 255, 255, 0.4)',
                            borderRadius: '6px',
                            padding: '0.5rem 0.75rem',
                            color: '#00ffff',
                            cursor: addingTrackId === (track.track_id || track.id) ? 'not-allowed' : 'pointer',
                            fontSize: '1.2rem',
                            opacity: addingTrackId === (track.track_id || track.id) ? 0.5 : 1,
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={(e) => {
                            if (addingTrackId !== (track.track_id || track.id)) {
                              e.currentTarget.style.background = 'rgba(0, 255, 255, 0.2)';
                              e.currentTarget.style.transform = 'scale(1.1)';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (addingTrackId !== (track.track_id || track.id)) {
                              e.currentTarget.style.background = 'rgba(0, 255, 255, 0.1)';
                              e.currentTarget.style.transform = 'scale(1)';
                            }
                          }}
                          title={`Add to ${targetCrateName}`}
                        >
                          {addingTrackId === (track.track_id || track.id) ? '⏳' : '📦'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaylistPickerModal;
